import type { ISurgeryDefinition } from '@/engine/interfaces/ISurgeryDefinition.ts';
import { type ContainerModule, inject } from 'inversify';
import { injectable } from 'inversify';
import type { CaseInfo } from '@/engine/app/CaseInfoApi.ts';
import type {
  IPresentationCreatorRegistrar,
  ISugerySaveStateUpdateSubscriber,
  JSONConfig
} from '@/engine/app/interfaces.ts';
import type { ICasePersistanceExtension } from '@/engine/app/CasePersistance/CasePersistenceHub.ts';
import type { ImporterConfig } from '@/engine/Serialization/IImporter.ts';
import type { UIConfigSchema } from '@/engine/app/modules/ui';
import { TYPES } from '@/engine/ioc';
import type { SliceOrientationsConfig } from '@/engine/app/modules/images/interfaces.ts';
import type { ReportFormat } from '../modules/reporting/ReportFormat.ts';
import type { IPickablesSelector } from '@/components/viewPort/PickablesSelector.ts';
import { DefaultSaveStateUpdateSubscriber } from '../DefaultSaveStateUpdateSubscriber.ts';

@injectable()
export class SurgeryRegistry {
  private readonly _registrar: IPresentationCreatorRegistrar;

  public constructor(@inject(TYPES.PresentationCreatorsRegistry) registrar: IPresentationCreatorRegistrar) {
    this._registrar = registrar;
  }

  private readonly _surgeryTypes: Map<string, ISurgeryDefinition> = new Map();

  private _surgeryDefinition?: ISurgeryDefinition;

  public registerSurgeryType(surgeryType: string, surgeryDefinition: ISurgeryDefinition): void {
    if (this._surgeryTypes.has(surgeryType)) {
      throw new Error(`Surgery ${surgeryType} already registered`);
    }
    this._surgeryTypes.set(surgeryType, surgeryDefinition);
  }

  public resolveSurgeryType(caseInfo: CaseInfo): string {
    if (this._surgeryTypes.has(caseInfo.surgeryType)) {
      return caseInfo.surgeryType;
    } else {
      throw new Error(`Unknown Surgery Type: [${caseInfo.surgeryType}]`);
    }
  }

  public async init(surgeryType: string): Promise<void> {
    this._surgeryDefinition = this._surgeryTypes.get(surgeryType);
    if (!this._surgeryDefinition) {
      throw new Error(`Surgery ${surgeryType} not registered`);
    }
    await this._surgeryDefinition.init();
  }

  public getCurrentSurgeryUiConfig(): UIConfigSchema {
    const surgeryDefinition: ISurgeryDefinition = this._getSurgeryDefinition();
    return surgeryDefinition.getUiConfig();
  }

  public getCurrentSurgeryViewsConfig(): JSONConfig.Config {
    const surgeryDefinition: ISurgeryDefinition = this._getSurgeryDefinition();
    return surgeryDefinition.getViewsConfig();
  }

  public getCurrentSurgerySliceOrientationsConfig(): SliceOrientationsConfig {
    const surgeryDefinition: ISurgeryDefinition = this._getSurgeryDefinition();
    return surgeryDefinition.getSliceOrientationsConfig();
  }

  public getCurrentSurgeryMCSDImporterConfig(): ImporterConfig {
    const surgeryDefinition: ISurgeryDefinition = this._getSurgeryDefinition();
    return surgeryDefinition.getMCSDImporterConfig();
  }

  public getCurrentSurgeryCasePersistanceExtension(): ICasePersistanceExtension | undefined {
    const surgeryDefinition: ISurgeryDefinition = this._getSurgeryDefinition();
    return surgeryDefinition.getCasePersistanceExtension();
  }

  public getCurrentSurgeryReportConfig(): ReportFormat | undefined {
    const surgeryDefinition: ISurgeryDefinition = this._getSurgeryDefinition();
    return surgeryDefinition.getReportConfig();
  }

  public getCurrentSurgerySavingStateUpdateSubscriptions(): ISugerySaveStateUpdateSubscriber {
    const surgeryDefinition: ISurgeryDefinition = this._getSurgeryDefinition();
    return surgeryDefinition.getSaveStateUpdateSubscriber?.() || new DefaultSaveStateUpdateSubscriber();
  }

  public getPickablesSelector(): IPickablesSelector {
    const surgeryDefinition: ISurgeryDefinition = this._getSurgeryDefinition();
    return surgeryDefinition.getPickablesSelector();
  }

  public getIoCModule(): ContainerModule {
    const surgeryDefinition: ISurgeryDefinition = this._getSurgeryDefinition();
    return surgeryDefinition.getIoCModule();
  }

  public registerPresentationCreators(): void {
    this._getSurgeryDefinition().getPresentationRegistrar().registerPresentations(this._registrar);
  }

  public async onModuleLoadedAsync(): Promise<void> {
    await this._getSurgeryDefinition().onModuleLoadedAsync?.();
  }

  private _getSurgeryDefinition(): ISurgeryDefinition {
    if (!this._surgeryDefinition) {
      throw new Error('Not initialized');
    }
    return this._surgeryDefinition;
  }
}
